/**
 * Multi-room hotel pricing chain SSOT — profile card through payment validator.
 */

import { roundMoney2 } from "../../pricing/pricingEngine";
import { getHotelSummaryLegs } from "../getHotelSummaryLegs";
import {
  buildAgentHotelMultiRoomPricingDisplay,
  isHotelProfileMultiRoomComboPackage,
} from "../pricing/resolveAgentHotelMultiRoomPricingDisplay";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "../hotelBookingPriceSsot";
import {
  buildHotelNewOrderTransportFields,
  resolveAgentHrOurPriceForBookingPayload,
} from "../hotelQuoteLinkCreatePayload";
import {
  buildDraftProfilePriceContextSnapshot,
  resolveHotelFinalCheckComboCustomerFacingTotal,
  resolveHotelProfileDisplayedComboTotalAtClick,
} from "./resolveHotelFinalCheckComboCustomerFacingTotal";

export const HOTEL_MULTIROOM_PRICE_CHAIN_TRACE = "[HOTEL_MULTIROOM_PRICE_CHAIN_TRACE]";

const CHAIN_TOLERANCE = 0.02;

function normalizeChainAmount(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return roundMoney2(n);
}

function chainAmountsEqual(a, b) {
  if (a == null || b == null) return a === b;
  const tol = Math.max(CHAIN_TOLERANCE, Math.abs(a) * 0.0001);
  return Math.abs(a - b) <= tol;
}

/** Rebuild agent profile context from persisted draft fields. */
export function buildProfilePriceContextFromOrder(order) {
  const snap = buildDraftProfilePriceContextSnapshot(order?.profilePriceContext);
  if (snap?.agentHotelPricing) return snap;

  return {
    selectedHotelCurrency:
      order?.displayCurrency ?? order?.searchPhrase?.currency ?? "ILS",
    hotelSearchListDisplayCurrency:
      order?.displayCurrency ?? order?.searchPhrase?.currency ?? "ILS",
    hotelId:
      order?.selectedHotel?.HotelId ??
      order?.selectedHotel?.hotelId ??
      order?.profilePriceContext?.hotelId ??
      null,
    agentHotelPricing: order?.profilePriceContext?.agentHotelPricing ?? null,
  };
}

export function isMultiRoomHotelOrderForPriceChain(order) {
  if (!order || typeof order !== "object") return false;
  const legsModel = getHotelSummaryLegs(order, { t: (k) => k });
  if (legsModel.isMultiRoom && legsModel.legs.length >= 2) return true;
  return isHotelProfileMultiRoomComboPackage(
    order.selectedRoom,
    order.selectedHotelPackage
  );
}

/**
 * @param {object} fields
 * @returns {{
 *   profileCardTotal: number|null,
 *   displayedPriceAtClick: number|null,
 *   availabilityCheckTotal: number|null,
 *   passengerPageTotal: number|null,
 *   paymentPageTotal: number|null,
 *   orderPayloadTotal: number|null,
 *   validatorExpectedTotal: number|null,
 *   allEqual: boolean,
 *   checkpoint?: string|null,
 *   packageId?: string|null,
 * }}
 */
export function buildHotelMultiRoomPriceChainSnapshot(fields = {}) {
  const chain = {
    profileCardTotal: normalizeChainAmount(fields.profileCardTotal),
    displayedPriceAtClick: normalizeChainAmount(fields.displayedPriceAtClick),
    availabilityCheckTotal: normalizeChainAmount(fields.availabilityCheckTotal),
    passengerPageTotal: normalizeChainAmount(fields.passengerPageTotal),
    paymentPageTotal: normalizeChainAmount(fields.paymentPageTotal),
    orderPayloadTotal: normalizeChainAmount(fields.orderPayloadTotal),
    validatorExpectedTotal: normalizeChainAmount(fields.validatorExpectedTotal),
    checkpoint: fields.checkpoint ?? null,
    packageId: fields.packageId ?? null,
  };

  const defined = [
    chain.profileCardTotal,
    chain.displayedPriceAtClick,
    chain.availabilityCheckTotal,
    chain.passengerPageTotal,
    chain.paymentPageTotal,
    chain.orderPayloadTotal,
    chain.validatorExpectedTotal,
  ].filter((v) => v != null);

  chain.allEqual =
    defined.length <= 1 ||
    defined.every((value) => chainAmountsEqual(value, defined[0]));

  return chain;
}

export function emitHotelMultiRoomPriceChainTrace(snapshot) {
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_MULTIROOM_PRICE_CHAIN_TRACE, {
      ts: Date.now(),
      ...snapshot,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Resolve chain totals from order + optional checkpoint overrides.
 */
export function resolveHotelMultiRoomPriceChainFromOrder(order, overrides = {}) {
  if (!order || typeof order !== "object") {
    return buildHotelMultiRoomPriceChainSnapshot(overrides);
  }

  const profilePriceContext = buildProfilePriceContextFromOrder(order);
  const pkg = order.selectedHotelPackage;
  const room = order.selectedRoom;
  const packageId =
    pkg?.packageID ?? pkg?.PackageId ?? pkg?.packageId ?? overrides.packageId ?? null;

  const comboResolved = resolveHotelFinalCheckComboCustomerFacingTotal({
    pkg,
    room,
    profilePriceContext,
    displayedPriceAtClick: order.displayedPriceAtClick,
  });

  const multiDisplay = buildAgentHotelMultiRoomPricingDisplay({
    currentHotelOrder: order,
    profilePriceContext,
  });
  const passengerLineTotal =
    multiDisplay.isMultiRoom && multiDisplay.totalIls != null
      ? multiDisplay.totalIls
      : resolveCustomerFacingHotelLineIlsFromOrder(order);

  const fullPriceFallback =
    overrides.fullPriceFallback ??
    order.ourPrice?.priceWithCreditCardFee ??
    order.ourPrice?.customerDisplayTotalIls ??
    null;
  const transport = buildHotelNewOrderTransportFields(order, fullPriceFallback);
  void transport;

  const agentHrOurPrice = resolveAgentHrOurPriceForBookingPayload(
    order,
    fullPriceFallback
  );
  const hotelLineSsot =
    agentHrOurPrice?.priceWithCommission ??
    passengerLineTotal ??
    resolveCustomerFacingHotelLineIlsFromOrder(order);
  const orderPayloadHotelLine = normalizeChainAmount(
    agentHrOurPrice?.priceWithCommission ?? hotelLineSsot
  );
  const orderPayloadTotal =
    overrides.orderPayloadTotal != null
      ? normalizeChainAmount(overrides.orderPayloadTotal)
      : orderPayloadHotelLine;
  const validatorExpectedTotal =
    overrides.validatorExpectedTotal != null
      ? normalizeChainAmount(overrides.validatorExpectedTotal)
      : orderPayloadHotelLine;

  const stored = order.multiRoomPriceChain ?? {};

  return buildHotelMultiRoomPriceChainSnapshot({
    profileCardTotal:
      overrides.profileCardTotal ??
      stored.profileCardTotal ??
      comboResolved.total,
    displayedPriceAtClick:
      overrides.displayedPriceAtClick ??
      order.displayedPriceAtClick ??
      comboResolved.total,
    availabilityCheckTotal:
      overrides.availabilityCheckTotal ??
      stored.availabilityCheckTotal ??
      null,
    passengerPageTotal:
      overrides.passengerPageTotal ?? passengerLineTotal,
    paymentPageTotal:
      overrides.paymentPageTotal ?? stored.paymentPageTotal ?? null,
    orderPayloadTotal:
      overrides.orderPayloadTotal ?? orderPayloadTotal,
    validatorExpectedTotal:
      overrides.validatorExpectedTotal ?? validatorExpectedTotal,
    checkpoint: overrides.checkpoint ?? null,
    packageId,
  });
}

/**
 * Emit chain trace for multi-room orders; merge checkpoint fields onto draft chain state.
 * @returns {object|null}
 */
export function traceHotelMultiRoomPriceChainAtCheckpoint(order, checkpoint, overrides = {}) {
  if (!isMultiRoomHotelOrderForPriceChain(order)) return null;

  const snapshot = resolveHotelMultiRoomPriceChainFromOrder(order, {
    ...overrides,
    checkpoint,
  });

  emitHotelMultiRoomPriceChainTrace(snapshot);

  return snapshot;
}

export function mergeMultiRoomPriceChainOntoDraft(order, patch = {}) {
  if (!order || typeof order !== "object") return order;
  return {
    ...order,
    multiRoomPriceChain: {
      ...(order.multiRoomPriceChain ?? {}),
      ...patch,
    },
  };
}

/**
 * Persist visible combo total + profile context; emit profile checkpoint trace.
 */
export function enrichHotelOrderDraftWithMultiRoomPriceChain(
  draft,
  {
    profilePriceContext = null,
    profileCardTotal = null,
    checkpoint = "profile_room_select",
  } = {}
) {
  if (!draft || typeof draft !== "object") return draft;

  const ctx =
    buildDraftProfilePriceContextSnapshot(profilePriceContext) ??
    buildProfilePriceContextFromOrder(draft);
  const visibleResolved = resolveHotelProfileDisplayedComboTotalAtClick({
    room: draft.selectedRoom,
    pkg: draft.selectedHotelPackage,
    profilePriceContext: ctx,
  });
  const visible =
    profileCardTotal ??
    draft.displayedPriceAtClick ??
    visibleResolved.total;

  const enriched = {
    ...draft,
    displayedPriceAtClick: visible,
    profilePriceContext: ctx,
    ...(visible != null &&
    draft.bookingSsotIls != null &&
    Math.abs(Number(draft.bookingSsotIls) - Number(visible)) > 0.5
      ? { bookingSsotIls: visible }
      : {}),
    multiRoomPriceChain: {
      ...(draft.multiRoomPriceChain ?? {}),
      profileCardTotal: visible,
      displayedPriceAtClick: visible,
    },
  };

  if (isMultiRoomHotelOrderForPriceChain(enriched)) {
    traceHotelMultiRoomPriceChainAtCheckpoint(enriched, checkpoint, {
      profileCardTotal: visible,
      displayedPriceAtClick: visible,
    });
  }

  return enriched;
}
